import { FormControl, Stack, Tooltip } from '@mui/material'
import { C1Icon } from 'components/core/icons/C1Icon'
import { C1FormLabel } from 'components/core/RHKFormComponents/C1FormLabel'
import { DurationSelect, periods } from 'components/core/time/duration'
import { AccessRequest } from 'components/pages/accessrequest/create/form-v2/RequestAccessFormV2'
import { Info } from 'lucide-react'
import { FC, useMemo } from 'react'
import { Control, Controller, FieldErrors, useController } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'

type AccessRequestFormDurationFieldProps = {
    control: Control<AccessRequest>
    errors: FieldErrors<AccessRequest>
}

export const AccessRequestFormDurationField: FC<AccessRequestFormDurationFieldProps> = ({
    control,
    errors,
}) => {
    const intl = useIntl()

    const { field: ents } = useController({
        name: 'ents',
        control,
    })

    // Infinity means - there is no max duration. When this is serialized into a request, it's set to 0: periods.INDEF
    // For comparison reasons, we use -Infinity as the inital value of the .reduce, so if there are no entitlements, we'll return -Infinity
    const maxDuration = useMemo(() => ents.value?.map((e) => (e.maxGrantDuration?.oneofKind === 'durationGrant'
        ? e.maxGrantDuration.durationGrant.seconds
        : Infinity
    )).reduce((a, b) => Math.max(a || 0, b || 0), -Infinity), [ents.value])

    // If either of the above conditions are true, it is safe to let the user select infinite duration (which is 0)
    const canSelectInfiniteDuration = maxDuration === Infinity || maxDuration === -Infinity

    const hasMultipleEntitlements = (ents.value?.length ?? 0) > 1

    return <FormControl error={!!errors.provisionDurationSeconds} fullWidth disabled={ents.value?.length === 0}>
        <Stack direction="row" spacing={1} alignItems="center">
            <C1FormLabel
                label={intl.formatMessage({
                    id: 'component_access_request_provisionDurationSeconds_label',
                    defaultMessage: 'How long is the access needed?',
                })}
                htmlFor="provisionDurationSeconds"
            />
            {hasMultipleEntitlements && (
                <Tooltip
                    title={
                        <FormattedMessage
                            id="component_access_request_duration_multiple_entitlements_tooltip"
                            defaultMessage="The maximum configured duration for each entitlement will still apply."
                        />
                    }
                    placement="top-start"
                >
                    <C1Icon icon={Info} size="sm" sx={{ color: 'info.main' }} />
                </Tooltip>
            )}
        </Stack>
        <Controller
            name="provisionDurationSeconds"
            control={control}
            render={({ field }): React.ReactElement => <DurationSelect
                id="provisionDurationSeconds"
                labelId="label-provisionDurationSeconds"
                disabled={ents.value?.length === 0}
                onChange={(seconds: number): void => {
                    field.onChange(seconds)
                }}
                onBlur={field.onBlur}
                value={field.value}
                error={!!errors.provisionDurationSeconds}
                maxDuration={canSelectInfiniteDuration ? periods.INDEF : maxDuration}
            />}
        />
    </FormControl>
}
